/* Atlas V3 Desktop — right-side ticker panel (Schwab-style dock).
   A non-modal slide-in on the right: the main page keeps its own scroll and
   stays interactive (content shifts left via the [data-v3web-panel] root attr).
   Reuses the SAME data + chart the mobile TickerSheet uses — the real OHLCV
   CandleChart (candles/line toggle + volume + crosshair), Atlas analysis,
   leveraged 2× ETFs, news sentiment, fundamentals, next earnings. Mobile's
   bottom-sheet is untouched. */
import { useEffect, useMemo, useRef, useState } from 'react'
import { StockLogo } from '@/v2/components/StockLogo'
import { CandleChart, OptionsTab } from '@/v2/v3/TickerSheet'
import { DEFAULT_INDICATORS } from '@/v2/v3/CandleChart'
import { ChartSettingsPanel, AdvancedChart } from '@/v2/v3/ChartSettings'
import { useUserPref } from '@/v2/hooks/useUserPref'
import { ANALYSIS_PAUSED } from '@/v2/lib/analysisPaused'
import { Chip, Pct, intent } from '@/v2/v3/lib'
import { useWatchlists } from '@/v2/hooks/useWatchlists'
import { useEarnings } from '@/v2/hooks/useSupabaseData'
import { useOvernightSplit } from '@/v2/hooks/useOvernightSplit'
import { stripNamePrefix } from '@/v2/lib/descriptionFormat'
import { Crossfade, SkelChart } from './qol'

const Mono = { fontFamily: 'var(--studio-font-mono)' }
const Serif = { fontFamily: 'var(--studio-font-display)' }
// Same alert_level vocabulary as mobile TickerSheet: urgent/warning/info
// (+ legacy red/yellow/green). 'none'/null → no badge via the render gate.
const ALERT_TOKEN = {
  urgent: '--studio-danger', warning: '--studio-warning', info: '--studio-text-muted',
  red: '--studio-danger', yellow: '--studio-warning', green: '--studio-success',
}
const TFS = ['1D', '5D', '1M', '6M', '1Y', '5Y', 'MAX']
// Candle-family types, so the CANDLES text toggle reflects hollow/heikin.
const CANDLE_KINDS = { candles: true, candle: true, hollow: true, heikin: true }
// See TickerSheet: ranges too short for the longer indicators, mapped to the
// lead-in range fetched alongside them.
const WARMUP_TF = { '1M': '1Y', '6M': '1Y' }
// Large 5Y/MAX percentages ("+950.1%") need a k-suffix past 1,000 to stay
// inside the Performance band's cell width — mirrors mobile TickerSheet's
// fmtPct.
const fmtPerfPct = (v) => {
  const a = Math.abs(v)
  return `${v < 0 ? '-' : '+'}${a >= 1000 ? `${(a / 1000).toFixed(1)}k` : a.toFixed(1)}%`
}

// Category enums seeded into the thesis column (e.g. AI_CLOUD) are labels, not
// prose — never render them as the Thesis paragraph. Mirrors mobile TickerSheet.
function isEnumThesis(s) {
  if (!s || typeof s !== 'string') return false
  return /^[A-Z][A-Z0-9_]+$/.test(s.trim())
}
function fmtMcap(m) {
  if (!m) return null
  const b = m / 1000
  if (b >= 1000) return `$${(b / 1000).toFixed(2)}T mkt cap` // mirror mobile fmtMcap — "$4970B" read wrong
  return b >= 1 ? `$${b.toFixed(b >= 100 ? 0 : 1)}B mkt cap` : `$${Math.round(m)}M mkt cap`
}
function useFundamentals(ticker) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  useEffect(() => {
    if (!ticker) { setData(null); setLoading(false); return }
    let cancelled = false
    setData(null); setLoading(true)
    fetch(`/api/fundamentals?ticker=${encodeURIComponent(ticker)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled) { if (d && !d.error) setData(d); setLoading(false) } })
      .catch(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [ticker])
  return { fund: data, loading }
}
/* Real OHLCV bars for the CandleChart — same fetch the mobile sheet uses. */
function useBars(ticker, range, interval) {
  const [bars, setBars] = useState(null)
  const [loading, setLoading] = useState(true)
  // The dock stays mounted across ticker switches (unlike the mobile sheet,
  // which remounts per open) — drop the old symbol's bars immediately so the
  // chart never paints the previous ticker while the new fetch is in flight.
  // Range changes keep the current bars (calm swap, mirrors mobile).
  useEffect(() => { setBars(null) }, [ticker])
  useEffect(() => {
    if (!ticker || !range) { setBars(null); setLoading(false); return }
    let cancelled = false
    const ctrl = new AbortController()
    setLoading(true)
    const q = `/api/chart-bars?ticker=${encodeURIComponent(ticker)}&range=${range}${interval ? `&interval=${interval}` : ''}`
    fetch(q, { signal: ctrl.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled) { setBars(Array.isArray(d?.bars) ? d.bars : null); setLoading(false) } })
      .catch((e) => { if (e.name !== 'AbortError' && !cancelled) setLoading(false) })
    return () => { cancelled = true; ctrl.abort() }
  }, [ticker, range, interval])
  return { bars, loading }
}
/* Leveraged 2× ETFs on this underlying + their live snapshot — mirrors mobile. */
function useLeveraged(ticker) {
  const [etfs, setEtfs] = useState([])
  const [prices, setPrices] = useState({})
  useEffect(() => {
    if (!ticker) { setEtfs([]); setPrices({}); return }
    let cancelled = false
    setEtfs([]); setPrices({})
    fetch(`/api/leveraged-etfs?underlying=${encodeURIComponent(ticker)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (cancelled) return
        const list = d?.instruments?.[0]?.etfs || []
        setEtfs(list)
        if (list.length) {
          fetch(`/api/prices?tickers=${list.map((e) => e.ticker).join(',')}&mode=snapshot`)
            .then((r) => (r.ok ? r.json() : null))
            .then((pd) => { if (!cancelled && pd?.prices) setPrices(pd.prices) })
            .catch(() => {})
        }
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [ticker])
  return { etfs, prices }
}

function Band({ label, right, children, enter = false }) {
  return (
    <div className={enter ? 'v3web-enter' : undefined} style={{ padding: '16px 20px', borderTop: '1px solid hsl(var(--studio-border) / 0.5)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ ...Mono, fontSize: 9, letterSpacing: '0.16em', color: 'hsl(var(--studio-text-subtle))' }}>{label}</div>
        {right}
      </div>
      {children}
    </div>
  )
}

export function TickerPanel({ stock, live = {}, onClose }) {
  const [tf, setTf] = useState('1D')
  const panelRef = useRef(null)
  const [type, setType] = useState('line') // opens as line, candles on toggle (mirrors mobile)
  // Shared with mobile under the same pref key — indicators follow the user
  // across surfaces rather than being set twice.
  const [indicators, setIndicators] = useUserPref('chart_indicators', DEFAULT_INDICATORS)
  const [scaleMode, setScaleMode] = useUserPref('chart_scale_mode', 'normal')
  const [interval, setInterval] = useUserPref('chart_interval', '5m')
  const [gearOpen, setGearOpen] = useState(false)
  const [advOpen, setAdvOpen] = useState(false)
  const [measure, setMeasure] = useState(false)
  const activeCount = Object.entries({ ...DEFAULT_INDICATORS, ...indicators })
    .filter(([k, v]) => v && k !== 'volume').length
  const [toast, setToast] = useState(null)
  const t = stock?.ticker
  const { bars, loading: barsLoading } = useBars(t, tf, interval)
  const { bars: warmup } = useBars(t, WARMUP_TF[tf] || null)
  // Usable bars in the range — indicators needing more are greyed out
  // rather than silently drawing nothing (1M is ~22 daily bars).
  const indSrc = (warmup && bars && warmup.length > bars.length) ? warmup : bars
  const barCount = Array.isArray(indSrc) ? indSrc.filter((b) => b && b.c != null).length : 0
  const { fund } = useFundamentals(t)
  const { earnings } = useEarnings()
  const { watchlists, active, addTicker, removeTicker } = useWatchlists()
  // 1D-1Y come from the live snapshot's pre-computed buckets. 5Y/MAX have no
  // snapshot bucket — before this fix the desktop panel didn't even offer
  // those chips (mobile TickerSheet already reads them from /api/overnight-split's
  // per-window total over price_history; this mirrors that fix here).
  const split = useOvernightSplit(t)
  const perf = useMemo(() => ({
    '1D': live.change1d, '5D': live.change1w ?? live.change5d, '1M': live.change1m, '6M': live.change6m, '1Y': live.change1y,
    '5Y': split.windows?.['5Y']?.total ?? null, 'MAX': split.windows?.MAX?.total ?? null,
  }), [live, split.windows])

  const watched = useMemo(() => !!t && (watchlists || []).some((w) => (w.tickers || []).includes(t)), [watchlists, t])
  // `active` defaults to the pseudo-list 'all' — resolve to a real list id or
  // the write 4xxs server-side after the toast already claimed success.
  const realLists = (watchlists || []).filter((w) => w.id && w.id !== 'all')
  const containingList = t ? realLists.find((w) => (w.tickers || []).includes(t)) : null
  const targetList = (active && active !== 'all') ? active : (containingList?.id ?? realLists[0]?.id)
  // Switching tickers while the dock is open: land at the top, fade the new
  // content in (the keyed wrapper below) instead of hard-swapping mid-scroll.
  useEffect(() => { if (panelRef.current) panelRef.current.scrollTop = 0 }, [t])

  const nextEarn = useMemo(() => {
    if (!t) return null
    const today = new Date(); today.setHours(0, 0, 0, 0)
    return (earnings || []).filter((e) => e.ticker === t).map((e) => ({ ...e, d: new Date(e.date + 'T00:00:00') }))
      .filter((e) => e.d >= today).sort((a, b) => a.d - b.d)[0] || null
  }, [earnings, t])

  if (!stock) return null

  const price = live.price
  const chg = perf[tf]                   // follows the timeframe chips
  // Colour falls back to the 1D direction when the selected window has no
  // value, so the accent doesn't flip on a missing number.
  const up = (chg ?? live.change1d ?? 0) >= 0
  const chartUp = bars && bars.length > 1 ? bars[bars.length - 1].c >= bars[0].c : up
  const alertTok = ALERT_TOKEN[stock.cron_alert] || null
  const oneLiner = stripNamePrefix(stock.one_liner, stock.name)
  const mcap = fmtMcap(stock.market_cap_usd_m)
  const thesis = stock.thesis
  const hasThesis = thesis && !isEnumThesis(thesis)
  const sentPct = stock.cron_sentiment_bullish_pct
  const sentiment = sentPct != null ? { pct: Math.round(sentPct), count: stock.cron_buzz_article_count } : null

  function flash(m) { setToast(m); setTimeout(() => setToast(null), 1700) }
  const toggleWatch = () => {
    if (!t || !targetList) { flash('Sign in to use watchlists'); return }
    if (watched) { removeTicker(targetList, t); flash(`${t} removed`) }
    else { addTicker(targetList, t); flash(`${t} added to watchlist`) }
  }

  return (
    <aside ref={panelRef} className="v3web-panel atlas-scroll" role="dialog" aria-label={`${t} detail`}>
      {/* header */}
      <div style={{ position: 'sticky', top: 0, zIndex: 2, background: 'hsl(var(--studio-surface))', borderBottom: '1px solid hsl(var(--studio-border) / 0.6)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '16px 18px 12px' }}>
          <StockLogo ticker={t} size={30} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ ...Mono, fontSize: 15, color: 'hsl(var(--studio-text))', letterSpacing: '0.02em' }}>{t}</div>
            <div style={{ fontSize: 11.5, color: 'hsl(var(--studio-text-subtle))', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{stock.name || stock.company}</div>
          </div>
          <button onClick={toggleWatch} aria-label="Watchlist" title={watched ? 'Remove from watchlist' : 'Add to watchlist'} style={{ all: 'unset', cursor: 'pointer', padding: 4, color: watched ? 'hsl(var(--studio-accent))' : 'hsl(var(--studio-text-subtle))' }}>
            <svg viewBox="0 0 24 24" width="18" height="18" fill={watched ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M12 17.75l-6.172 3.245l1.179 -6.873l-5 -4.867l6.9 -1l3.086 -6.253l3.086 6.253l6.9 1l-5 4.867l1.179 6.873l-6.158 -3.245" /></svg>
          </button>
          <button onClick={onClose} aria-label="Close" style={{ all: 'unset', cursor: 'pointer', padding: 4, color: 'hsl(var(--studio-text-muted))', fontSize: 16, lineHeight: 1 }}>✕</button>
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, padding: '0 18px 14px' }}>
          <span style={{ ...Serif, fontStyle: 'italic', fontSize: 34, lineHeight: 0.95, color: 'hsl(var(--studio-text))' }}>
            {price != null ? `$${Number(price).toFixed(2)}` : '—'}
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 5, paddingBottom: 4 }}>
            {/* Multi-year returns render k-suffixed to match the Performance
                band below (a MAX value as "+2143.20%" next to the band's
                "+2.1k%" reads as two different numbers). The count-up Pct is
                kept for every normal-magnitude window, where it's legible. */}
            {chg == null
              ? <span style={{ ...Mono, fontSize: 15, color: 'hsl(var(--studio-text-subtle))' }}>—</span>
              : Math.abs(chg) >= 1000
                ? <span style={{ ...Mono, fontSize: 15, color: `hsl(var(${intent(up)}))`, fontVariantNumeric: 'tabular-nums' }}>{fmtPerfPct(chg)}</span>
                : <Pct value={chg} style={{ ...Mono, fontSize: 15, color: `hsl(var(${intent(up)}))` }} />}
            <span style={{ ...Mono, fontSize: 10, color: 'hsl(var(--studio-text-subtle))' }}>{tf}</span>
          </span>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', padding: '0 18px 12px' }}>
          {stock.tier && stock.tier !== 'main' && <Badge tok="--studio-accent">TIER · {String(stock.tier).toUpperCase()}</Badge>}
          {alertTok && stock.cron_alert !== 'green' && stock.cron_alert !== 'none' && <Badge tok={alertTok}>ALERT · {String(stock.cron_alert).toUpperCase()}</Badge>}
          {mcap && <Badge tok="--studio-text-subtle">{mcap}</Badge>}
        </div>
      </div>

      {/* keyed by ticker: switching tickers fades the new content in */}
      <div key={t} className="v3web-enter">
      {/* chart — the real OHLCV CandleChart (shared with mobile). The small
          text toggle cycles LINE → CANDLES → OPTIONS: the third mode swaps the
          chart for the options chain (shared OptionsTab), everything below
          stays. Kept as quiet text links, not buttons (Ben: nothing huge). */}
      <div style={{ padding: '14px 12px 4px' }}>
        {type === 'options' ? (
          <div style={{ padding: '0 6px' }}><OptionsTab stock={stock} /></div>
        ) : (
          <Crossfade loading={!bars && barsLoading} skeleton={<SkelChart height={190} />}>
            <CandleChart bars={bars} warmup={warmup} type={type} up={chartUp} height={190} tf={tf} indicators={indicators}
              scaleMode={scaleMode} measure={measure} />
          </Crossfade>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 6, padding: '0 6px', position: 'relative' }}>
          {type !== 'options' && TFS.map((l) => <Chip key={l} active={tf === l} onClick={() => setTf(l)}>{l}</Chip>)}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginLeft: 'auto' }}>
            {[['line', 'LINE'], ['candle', 'CANDLES'], ['options', 'OPTIONS']].map(([m, label]) => {
              // CANDLES stays lit when the settings panel picks a candle
              // variant (hollow / heikin), which aren't in this text toggle.
              const on = type === m || (m === 'candle' && CANDLE_KINDS[type])
              return (
                <button key={m} onClick={() => setType(m)} style={{ all: 'unset', cursor: 'pointer', ...Mono, fontSize: 9.5, letterSpacing: '0.1em',
                  color: on ? 'hsl(var(--studio-accent))' : 'hsl(var(--studio-text-subtle))' }}>
                  {label}
                </button>
              )
            })}
            {type !== 'options' && (
              <>
                <button onClick={() => setGearOpen(!gearOpen)} aria-label="Chart settings" aria-expanded={gearOpen} title="Chart settings"
                  style={{ all: 'unset', cursor: 'pointer', ...Mono, fontSize: 11, lineHeight: 1,
                    color: `hsl(var(--studio-${gearOpen || activeCount ? 'accent' : 'text-subtle'}))` }}>
                  ⚙{activeCount ? <sup style={{ fontSize: 7, marginLeft: 1 }}>{activeCount}</sup> : null}
                </button>
                <button onClick={() => setMeasure(!measure)} aria-label="Measure" aria-pressed={measure} title="Measure"
                  style={{ all: 'unset', cursor: 'pointer', ...Mono, fontSize: 11, lineHeight: 1,
                    color: `hsl(var(--studio-${measure ? 'accent' : 'text-subtle'}))` }}>⇔</button>
                <button onClick={() => setAdvOpen(true)} aria-label="Expand chart" title="Expand chart"
                  style={{ all: 'unset', cursor: 'pointer', ...Mono, fontSize: 11, lineHeight: 1, color: 'hsl(var(--studio-text-subtle))' }}>⤢</button>
              </>
            )}
          </div>
          {gearOpen && (
            <div style={{ position: 'absolute', top: 24, right: 6, zIndex: 40 }}>
              <ChartSettingsPanel type={type} setType={setType} indicators={indicators} setIndicators={setIndicators} onClose={() => setGearOpen(false)} barCount={barCount} scaleMode={scaleMode} setScaleMode={setScaleMode}
                tf={tf} interval={interval} setInterval={setInterval} />
            </div>
          )}
        </div>
        {advOpen && (
          <AdvancedChart ticker={t} bars={bars} warmup={warmup} tf={tf} setTf={setTf} timeframes={TFS}
            type={type === 'options' ? 'line' : type} setType={setType}
            indicators={indicators} setIndicators={setIndicators} up={chartUp} onClose={() => setAdvOpen(false)} desktop
            scaleMode={scaleMode} setScaleMode={setScaleMode} interval={interval} setInterval={setInterval} />
        )}
      </div>

      {/* performance grid */}
      <Band label="Performance">
        <div style={{ display: 'flex', gap: 4 }}>
          {TFS.map((l) => {
            const v = perf[l]
            return (
              <div key={l} style={{ flex: 1, minWidth: 0, textAlign: 'center' }}>
                <div style={{ ...Mono, fontSize: 9, color: 'hsl(var(--studio-text-subtle))', marginBottom: 4 }}>{l}</div>
                <div style={{ ...Mono, fontSize: 12, fontVariantNumeric: 'tabular-nums', color: v == null ? 'hsl(var(--studio-text-subtle))' : `hsl(var(${intent(v >= 0)}))` }}>
                  {v == null ? '—' : fmtPerfPct(v)}
                </div>
              </div>
            )
          })}
        </div>
      </Band>

      {/* Atlas analysis */}
      {!ANALYSIS_PAUSED && (stock.cron_summary || hasThesis || stock.thesis_risk || stock.notes) && (
        <Band label="Atlas · Latest Analysis" right={stock.last_analyzed ? (
          <span style={{ ...Mono, fontSize: 8.5, letterSpacing: '0.08em', color: 'hsl(var(--studio-text-subtle))' }}>
            UPDATED {new Date(stock.last_analyzed).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }).toUpperCase()}
          </span>
        ) : null}>
          {stock.cron_summary && <p style={{ fontSize: 13, lineHeight: 1.6, color: 'hsl(var(--studio-text-muted))', margin: 0 }}>{stock.cron_summary}</p>}
          {hasThesis && <Sub head="Thesis" body={thesis} />}
          {stock.thesis_risk && <Sub head="Risk" body={stock.thesis_risk} />}
          {stock.notes && <Sub head="Notes" body={stock.notes} />}
        </Band>
      )}

      {/* news sentiment */}
      {sentiment && (
        <Band enter label="News Sentiment">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ ...Mono, fontSize: 18, fontVariantNumeric: 'tabular-nums', color: `hsl(var(${intent(sentiment.pct >= 50)}))` }}>{sentiment.pct}%</span>
            <div style={{ flex: 1, height: 3, borderRadius: 2, background: 'hsl(var(--studio-border))', overflow: 'hidden' }}>
              <div style={{ width: `${sentiment.pct}%`, height: '100%', background: `hsl(var(${intent(sentiment.pct >= 50)}))` }} />
            </div>
            <span style={{ ...Mono, fontSize: 9.5, color: 'hsl(var(--studio-text-subtle))', letterSpacing: '0.06em' }}>
              BULLISH{sentiment.count ? ` · ${sentiment.count} ARTICLES` : ''}
            </span>
          </div>
        </Band>
      )}

      {/* leveraged 2× */}
      <LeveragedBand ticker={t} />

      {/* what they do */}
      {(oneLiner || stock.what_they_do || stock.tidbit) && (
        <Band label="What they do">
          {(stock.what_they_do || oneLiner) && <p style={{ fontSize: 13, lineHeight: 1.6, color: 'hsl(var(--studio-text-muted))', margin: 0 }}>{stock.what_they_do || oneLiner}</p>}
          {stock.tidbit && <Sub head="Did you know" body={stock.tidbit} />}
        </Band>
      )}

      {/* key stats — fades in when Finnhub reports; absent for many tickers,
          so no skeleton (a skeleton that then vanishes reads as broken) */}
      <KeyStatsBand fund={fund} stock={stock} />

      {/* next earnings */}
      {nextEarn && (
        <Band enter label="Next Earnings">
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
            {/* mono, text color: dates are numerics (type-role rule) and amber
                at 22px crossed the no-large-amber-text line */}
            <span style={{ ...Mono, fontSize: 20, fontVariantNumeric: 'tabular-nums', color: 'hsl(var(--studio-text))' }}>{new Date(nextEarn.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
            <span style={{ fontSize: 12.5, color: 'hsl(var(--studio-text-muted))' }}>Earnings{nextEarn.time ? ` · ${({ bmo: 'before open', amc: 'after close' }[nextEarn.time] || nextEarn.time)}` : ''}{nextEarn.quarter ? ` · ${nextEarn.quarter}` : ''}</span>
          </div>
        </Band>
      )}

      </div>

      <div style={{ height: 24 }} />

      {toast && (
        <div style={{ position: 'sticky', bottom: 16, alignSelf: 'center', ...Mono, fontSize: 11, letterSpacing: '0.04em', color: 'hsl(var(--studio-text))', background: 'hsl(var(--studio-surface-2))', border: '1px solid hsl(var(--studio-border))', borderRadius: 999, padding: '7px 16px', boxShadow: '0 8px 24px -8px hsl(var(--studio-overlay))' }}>
          {toast}
        </div>
      )}
    </aside>
  )
}

function LeveragedBand({ ticker }) {
  const { etfs, prices } = useLeveraged(ticker)
  if (!etfs.length) return null
  return (
    <Band enter label="Leveraged · 2× Daily" right={
      <span style={{ ...Mono, fontSize: 8.5, letterSpacing: '0.1em', color: 'hsl(var(--studio-success))', display: 'flex', alignItems: 'center', gap: 5 }}>
        <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'hsl(var(--studio-success))' }} />LIVE
      </span>
    }>
      {etfs.map((e, i) => {
        const chg = prices[e.ticker]?.change1d
        const eUp = chg != null && chg >= 0
        const col = chg == null ? '--studio-text-subtle' : eUp ? '--studio-success' : '--studio-danger'
        return (
          <div key={e.ticker} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '11px 0', borderTop: i ? '1px solid hsl(var(--studio-border) / 0.4)' : 'none' }}>
            <span style={{ ...Mono, fontSize: 13, color: 'hsl(var(--studio-text))', minWidth: 52 }}>{e.ticker}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 11, color: 'hsl(var(--studio-text-muted))' }}>{e.issuer} {e.leverage}x Long {ticker}</div>
              {e.expense_ratio != null && <div style={{ ...Mono, fontSize: 8.5, letterSpacing: '0.06em', color: 'hsl(var(--studio-text-subtle))', marginTop: 2 }}>EXPENSE {e.expense_ratio}%</div>}
            </div>
            <span style={{ ...Mono, fontSize: 8, letterSpacing: '0.06em', color: 'hsl(var(--studio-accent))', border: '1px solid hsl(var(--studio-accent) / 0.3)', borderRadius: 3, padding: '1px 5px', flexShrink: 0 }}>{e.leverage}×</span>
            <span style={{ ...Mono, fontSize: 12.5, fontVariantNumeric: 'tabular-nums', color: `hsl(var(${col}))`, minWidth: 58, textAlign: 'right' }}>
              {chg == null ? '—' : `${eUp ? '+' : ''}${chg.toFixed(2)}%`}
            </span>
          </div>
        )
      })}
    </Band>
  )
}

function Badge({ tok, children }) {
  return (
    <span style={{ ...Mono, fontSize: 8.5, letterSpacing: '0.1em', padding: '3px 7px', borderRadius: 'var(--studio-radius-sm)', border: `1px solid hsl(var(${tok}) / 0.4)`, color: `hsl(var(${tok}))` }}>{children}</span>
  )
}
function Sub({ head, body }) {
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ ...Mono, fontSize: 8.5, letterSpacing: '0.12em', color: 'hsl(var(--studio-accent))', marginBottom: 5 }}>{head.toUpperCase()}</div>
      <p style={{ fontSize: 12.5, lineHeight: 1.55, color: 'hsl(var(--studio-text-muted))', margin: 0 }}>{body}</p>
    </div>
  )
}
/* Only render metrics that actually exist — a wall of "—" reads as broken.
   The band hides itself entirely when nothing is reported. */
function KeyStatsBand({ fund, stock }) {
  if (!fund) return null
  // api/fundamentals returns pre-formatted strings ("+12.3%", "31.4") —
  // parseFloat handles the sign/percent; Number() rejected them, which is why
  // this band silently showed seed columns only.
  const num = (v) => { const n = typeof v === 'string' ? parseFloat(v) : v; return n != null && !Number.isNaN(n) ? n : null }
  const stats = [
    // Live Finnhub only — no fallback to the seed-era stock.rev/stock.pe
    ['Rev growth', num(fund.revenueGrowth), '%'],
    ['P/E', num(fund.pe), ''],
    ['Net margin', num(fund.netMargin), '%'],
    ['ROE', num(fund.roe), '%'],
    ['Beta', num(fund.beta), ''],
  ].filter(([, v]) => v != null)
  if (!stats.length) return null
  return (
    <Band enter label="Key Stats">
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 20px' }}>
        {stats.map(([label, v, suffix]) => (
          <div key={label}>
            <div style={{ ...Mono, fontSize: 9, letterSpacing: '0.08em', color: 'hsl(var(--studio-text-subtle))', marginBottom: 3 }}>{label.toUpperCase()}</div>
            <div style={{ ...Mono, fontSize: 15, color: 'hsl(var(--studio-text))', fontVariantNumeric: 'tabular-nums' }}>{`${v.toFixed(suffix === '%' ? 1 : 2)}${suffix}`}</div>
          </div>
        ))}
      </div>
    </Band>
  )
}
